import EastIcon from '@mui/icons-material/East';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useGetLastMessages from '../../hooks/messages/useGetLastMessages';
import useSetMessageToSend from '../../hooks/messages/useSetMessageToSend';

const messagesTypes = [
  {
    label: 'À envoyer',
    value: 'TOSEND',
  },
  {
    label: "En cours d'envoi",
    value: 'SENDING',
  },
  {
    label: 'Envoyés',
    value: 'SENT',
  },
  {
    label: 'Reçus',
    value: 'RECEIVED',
  },
  {
    label: 'Brouillons',
    value: 'DRAFT',
  },
];

const ITEMS_PER_PAGE = 10;
const border = '1px solid #e0e0e0';

// Composant pour l'en-tête du tableau
const MessagesTableHeader = ({ contentTab, gridColumns }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: gridColumns,
      borderBottom: border,
      p: 1,
      fontWeight: 'bold',
      alignItems: 'center',
    }}
  >
    {contentTab === 'SENDING' && <div>Action</div>}
    <div>Date d&apos;envoi</div>
    <div>De</div>
    <div>À</div>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      Message ID
    </div>
    <div style={{ textAlign: 'center' }}>Aperçu</div>
    <div style={{ textAlign: 'center' }}>Pièces jointes</div>
    <div>Ticket</div>
  </Box>
);

// Composant pour une ligne de message
const MessageRow = ({
  message,
  contentTab,
  gridColumns,
  onSendMessage,
  toSendLoading,
  onNavigateToTicket,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: gridColumns,
      borderBottom: border,
      p: 1,
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    }}
  >
    {contentTab === 'SENDING' && (
      <SendMessageButton
        messageId={message._id}
        loading={toSendLoading === message._id}
        onSendMessage={onSendMessage}
      />
    )}
    <MessageDetails message={message} onNavigateToTicket={onNavigateToTicket} />
  </Box>
);

// Composant pour le bouton d'envoi
const SendMessageButton = ({ messageId, loading, onSendMessage }) => (
  <div>
    <Button
      disabled={loading}
      size="small"
      variant="contained"
      onClick={() => onSendMessage(messageId)}
      sx={{
        fontSize: '0.6rem',
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        px: 1,
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <span>Chargement</span>
        </>
      ) : (
        'Définir "à envoyer"'
      )}
    </Button>
  </div>
);

// Composant pour les détails du message
const MessageDetails = ({ message, onNavigateToTicket }) => (
  <>
    <div style={{ whiteSpace: 'nowrap' }}>
      {message?.sentAt
        ? new Date(message?.sentAt).toLocaleString('fr-FR')
        : 'Non envoyé'}
    </div>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {message.from}
    </div>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {message.to}
    </div>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {message._id}
    </div>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <Tooltip title={message.snippet}>
        <VisibilityIcon sx={{ cursor: 'pointer' }} />
      </Tooltip>
    </div>
    <div style={{ textAlign: 'center' }}>
      {message.attachments?.length || 0}
    </div>
    <div>
      <Button
        startIcon={<EastIcon />}
        size="small"
        variant="outlined"
        onClick={() =>
          onNavigateToTicket(message?.ticket?.project, message?.ticket?._id)
        }
        sx={{
          fontSize: '0.6rem',
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          px: 1,
        }}
      >
        {message?.ticket?.reference}
      </Button>
    </div>
  </>
);

const LastMessages = () => {
  const [contentTab, setContentTab] = useState('TOSEND');
  const [page, setPage] = useState(1);
  const [toSendLoading, setToSendLoading] = useState(false);
  const { setMessageToSend } = useSetMessageToSend();
  const { isLoading, messagesDatas, mutate, isValidating } = useGetLastMessages(
    {
      messageType: contentTab,
      page: page,
      limit: ITEMS_PER_PAGE,
    }
  );
  const navigate = useNavigate();

  const handleChange = (event, newValue) => {
    setContentTab(newValue);
    setPage(1); // Reset page when changing tab
  };

  useEffect(() => {
    // Déclencher le mutate quand contentTab ou page change
    mutate();
  }, [contentTab, page, mutate]);

  const handleSendMessage = async (messageId) => {
    setToSendLoading(messageId);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await setMessageToSend(messageId);
      mutate();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du message:', error);
    } finally {
      setToSendLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleNavigateToTicket = (projectId, ticketId) => {
    navigate(`/project/${projectId}/tickets/${ticketId}/activities`);
  };

  // Définir les colonnes en fonction du type d'onglet
  const getGridColumns = () => {
    if (contentTab === 'SENDING') {
      // Avec colonne Action
      return '12% 12% 17% 17% 17% 5% 5% 15%';
    }
    // Sans colonne Action
    return '12% 18% 18% 22% 5% 5% 20%';
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography
        variant="h5"
        sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}
      >
        Derniers Messages
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={contentTab}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            {messagesTypes.map((messageType) => (
              <Tab
                key={messageType.value}
                label={messageType.label}
                value={messageType.value}
              />
            ))}
          </Tabs>
        </Box>

        <Paper sx={{ mt: 2, p: 2 }}>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {!isLoading && !isValidating && (
              <Typography variant="body2">
                {messagesDatas?.count || 0} message
                {messagesDatas?.count > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          <MessagesTableHeader
            contentTab={contentTab}
            gridColumns={getGridColumns()}
          />

          {isLoading || isValidating ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={20} /> Chargement...
            </Box>
          ) : (
            <>
              {messagesDatas?.messages?.map((message) => (
                <MessageRow
                  key={message._id}
                  message={message}
                  contentTab={contentTab}
                  gridColumns={getGridColumns()}
                  onSendMessage={handleSendMessage}
                  toSendLoading={toSendLoading}
                  onNavigateToTicket={handleNavigateToTicket}
                />
              ))}

              {messagesDatas?.totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    pt: 2,
                    borderTop: border,
                  }}
                >
                  <Pagination
                    count={messagesDatas.totalPages}
                    page={messagesDatas.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default LastMessages;
